import { useCallback, useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';

import Tabs from '@/components/Tabs';
import { PageInformationContext } from '@/context/PageInformationContext';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useDocumentTitleRename from '@/hooks/useDocumentTitleRename';
import useUserDetails from '@/hooks/useUserDetails';
import TabPanelContent from '@/pages/DataHub/components/TabPanelContent';
import EditableName from '@/pages/DataHub/EditableName';
import useDataHubTabs from '@/pages/DataHub/hooks/useDataHubTabs';
import { setActiveDataHubId } from '@/slices/dataHubSlice';

import ActionBar from './ActionBar';
import PremiumFeatureMessage from './components/PremiumFeatureMessage';
import { NavigationContext } from './NavigationContext';
import useDataHub from './useDataHub';

const pageInformation = {
  mainPageTitle: 'Data Hub'
};

const DataHubPage = () => {
  const { activeTab, setActiveTab } = useDataHub();
  const dataHubs = useAppSelector(state => state.dataHub.dataHubs);
  const activeDataHubId = useAppSelector(state => state.dataHub.activeDataHubId);
  const status = useAppSelector(state => state.dataHub.status);

  const userDetails = useUserDetails();
  const isUserExpired = userDetails?.isUserExpired ?? false;
  const isUserPremium = userDetails?.isUserPremium ?? false;
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Memoize active data hub to prevent unnecessary re-renders
  const activeDataHub = useMemo(
    () => dataHubs.find(dataHub => dataHub.id === activeDataHubId),
    [dataHubs, activeDataHubId]
  );

  // Extract name from active data hub or use empty string as default
  const name = activeDataHub?.name ?? '';
  // Derived state values - memoized to prevent unnecessary recalculations
  const isDataHubLoading = status === 'loading';
  const isFeatureDisabled = isUserExpired || !isUserPremium;
  const disabled = useMemo(() => isDataHubLoading, [isDataHubLoading]);
  const dataHubTabs = useDataHubTabs(disabled);
  const navigate = useNavigate();
  const navigateToDataHub = useCallback(
    (index: number) => {
      navigate(`/data-hub/${activeDataHubId}/${dataHubTabs[index].name.toLowerCase()}`, {
        preventScrollReset: true,
        replace: false
      });
    },
    [dataHubTabs, navigate, activeDataHubId]
  );
  const navigationContextValue = useMemo(() => ({ navigateToDataHub }), [navigateToDataHub]);
  useEffect(() => {
    const paths = location.pathname.split('/');
    if (paths.length >= 3) {
      dataHubTabs.forEach(({ name }, index) => {
        if (paths[paths.length - 1].toLowerCase().includes(name.toLowerCase())) {
          setActiveTab(index);
        }
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const paths = location.pathname.split('/');
    if (activeDataHubId.length) {
      if (paths[2] && paths[2] === activeDataHubId) {
        return;
      }
      navigate(`/data-hub/${activeDataHubId}/${dataHubTabs[activeTab].name.toLowerCase()}`);
    }
  }, [activeDataHubId]);

  useEffect(() => {
    if (!activeDataHub && dataHubs.length) {
      const paths = location.pathname.split('/');
      if (activeDataHubId.length && paths.length === 4) {
        const id = paths[2];
        if (id && dataHubs.find(dh => dh.id === id)) {
          dispatch(setActiveDataHubId(id));
          return;
        }
      }
      dispatch(setActiveDataHubId(dataHubs[0]?.id));
    }
  }, [dataHubs, activeDataHub, dispatch]);

  useDocumentTitleRename('GX Go | Data Hub');

  return (
    <NavigationContext.Provider value={navigationContextValue}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
          <Typography
            noWrap
            sx={{ mr: 3 }}
            variant='h2'
          >
            Data Hub
          </Typography>
          {!isFeatureDisabled && name && <EditableName />}
        </Box>
        <PageInformationContext.Provider value={pageInformation}>
          <Tabs
            activeTab={activeTab}
            minTabWidth={145}
            tabs={dataHubTabs}
            onChange={navigateToDataHub}
          />
        </PageInformationContext.Provider>
      </Box>
      <ActionBar
        activeTab={activeTab}
        disabled={isFeatureDisabled}
      />
      <Box sx={{ flex: 1, display: 'flex', position: 'relative' }}>
        {isFeatureDisabled && activeTab !== 3 && <PremiumFeatureMessage />}

        <TabPanelContent
          key={activeDataHubId}
          activeDataHub={activeDataHub}
          activeTab={activeTab}
          status={status}
        />
      </Box>
    </NavigationContext.Provider>
  );
};

export default DataHubPage;
